/**
 * FieldOriginSection — shown in edit mode of the field details panel.
 *
 * Displays the chain of templates a field was inherited from, resolved
 * via `field.protoChain` against the lists already loaded by the
 * ListProvider (allLists includes the primary list + its
 * dependency templates).
 *
 * Spec: docs/CHECKLISTS_MIGRATION_PLAN.md:596-612
 *   This field is inherited from the following template(s)
 *     - @ Electrical installation Grow Cell
 */

import { StackLink } from '@/app/navigation/StackLink';
import type { Field } from '@certhon/models';
import { AtSign } from 'lucide-react';
import { useListContext } from '../context';

interface FieldOriginSectionProps {
  field: Field;
}

export function FieldOriginSection({ field }: FieldOriginSectionProps) {
  const { allLists } = useListContext();

  if (!('protoChain' in field) || field.protoChain.length === 0) {
    return null;
  }

  // Resolve each protoChain entry against allLists. Closest ancestor first
  // (the template the field was directly expanded from) — that's the order
  // the reducer pushes them on EXPAND_PROTO.
  const origins = field.protoChain.map((ref) => {
    const list = allLists.find((l) => l.id === ref.listId);
    return {
      listId: ref.listId,
      name: list?.name ?? `Template (${ref.listId.slice(0, 8)})`,
      isTemplate: list ? list.werk_id == null : true,
    };
  });

  return (
    <section className="mt-2 space-y-2">
      <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        This field is inherited from the following template(s)
      </h3>
      <ul className="divide-border divide-y rounded-md border">
        {origins.map((origin) => (
          <li key={origin.listId}>
            <StackLink
              to={
                origin.isTemplate
                  ? `/templates/${origin.listId}`
                  : `/lists/${origin.listId}`
              }
              className="hover:bg-muted/40 flex items-center gap-2 px-3 py-2 text-sm no-underline"
            >
              <AtSign className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate">{origin.name}</span>
            </StackLink>
          </li>
        ))}
      </ul>
    </section>
  );
}
